import React, { useEffect, useState } from 'react';
import { useSEO } from '../../hooks/useSEO';
import './FormPage.scss';

export interface FormField {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}

interface FormPageProps {
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  eyebrow?: string;
  heading: string;
  intro?: string;
  fields: FormField[];
  subject: string;
  submitLabel: string;
  submittingLabel?: string;
  successTitle: string;
  successText: string;
  noindex?: boolean;
}

// Static Forms — mesmo endpoint/apiKey do fluxo de inscrição.
const SF_ENDPOINT = 'https://api.staticforms.dev/submit';
const SF_API_KEY = '88d22151-70a0-4a81-9f0d-c35e3cf8e10b';

type Status = 'idle' | 'loading' | 'success' | 'error';

const FormPage: React.FC<FormPageProps> = ({
  seoTitle, seoDescription, canonical, eyebrow, heading, intro,
  fields, subject, submitLabel, submittingLabel = 'Enviando...',
  successTitle, successText, noindex = false,
}) => {
  useSEO({ title: seoTitle, description: seoDescription, canonical });
  const [status, setStatus] = useState<Status>('idle');

  // Páginas utilitárias (descadastro/gravação) não devem ser indexadas.
  useEffect(() => {
    if (!noindex) return;
    let el = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = !el;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, nofollow');
    return () => { if (created) el?.remove(); };
  }, [noindex]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    setStatus('loading');
    try {
      const res = await fetch(SF_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (res.ok) setStatus('success');
      else throw new Error('HTTP ' + res.status);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="form-page">
      <div className="form-page__card">
        {status === 'success' ? (
          <div className="form-page__success">
            <div className="form-page__check"><i className="fa-solid fa-circle-check" aria-hidden="true" /></div>
            <h1>{successTitle}</h1>
            <p>{successText}</p>
          </div>
        ) : (
          <>
            {eyebrow && <span className="form-page__eyebrow">{eyebrow}</span>}
            <h1 className="form-page__heading">{heading}</h1>
            {intro && <p className="form-page__intro">{intro}</p>}

            <form onSubmit={handleSubmit} noValidate>
              <input type="hidden" name="apiKey" value={SF_API_KEY} />
              <input type="hidden" name="subject" value={subject} />
              <input
                type="text" name="honeypot" tabIndex={-1} autoComplete="off"
                aria-hidden="true" className="form-page__honeypot"
              />

              {fields.map((f) => (
                <div key={f.name} className="form-page__field">
                  <label htmlFor={`fp-${f.name}`}>{f.label}</label>
                  <input
                    id={`fp-${f.name}`}
                    type={f.type || 'text'}
                    name={f.name}
                    required={f.required}
                    placeholder={f.placeholder}
                  />
                </div>
              ))}

              <button type="submit" disabled={status === 'loading'}>
                {status === 'loading' ? submittingLabel : submitLabel}
              </button>
              {status === 'error' && (
                <p className="form-page__error">Ops, não consegui enviar agora. Tente novamente em instantes.</p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default FormPage;
